'use client';

import { useMutation } from '@tanstack/react-query';

import { useCallback } from 'react';

import { useAppCallbacks } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { updateUsername } from '@/shared/services/user/user';

import { nameSchema } from '@/utils/schema';

type CaptureValidation = { ok: true; error: '' } | { ok: false; error: string };

/**
 * Headless name capture: owns name validation (canonical `nameSchema`) and the
 * `updateUsername` mutation. `submit` awaits the mutation then refetches userDetails so
 * the gate re-evaluates — callers hold a loading state across it. Workspace creation is
 * no longer coupled here; the gate provisions it in the background afterwards. Consumed
 * by the onboarding name screen.
 */
export function useNameCapture() {
	const refetchUserDetails = useAppCallbacks((state) => state.refetchUserDetails);
	const { mutateAsync } = useMutation({ mutationFn: updateUsername });

	const validate = useCallback((name: string): CaptureValidation => {
		const result = nameSchema.safeParse({ name: name.trim() });
		if (result.success) return { ok: true, error: '' };
		return {
			ok: false,
			error: result.error.flatten().fieldErrors.name?.[0] ?? 'Please enter a valid name.',
		};
	}, []);

	const submit = useCallback(
		async (name: string) => {
			await mutateAsync({ name: name.trim() });
			await refetchUserDetails();
		},
		[mutateAsync, refetchUserDetails]
	);

	return { validate, submit };
}
